import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const isConfigured = Boolean(supabaseUrl && supabaseAnonKey);

// Debug logging - remove after fixing
// console.log('Supabase config check:', {
//   hasUrl: !!supabaseUrl,
//   hasKey: !!supabaseAnonKey,
//   isConfigured,
//   urlPrefix: supabaseUrl?.substring(0, 20)
// });

if (!isConfigured) {
  // console.warn('Supabase credentials not found. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
}

// Create Supabase client only if properly configured
let supabase: SupabaseClient;

if (isConfigured) {
  supabase = createClient(
    supabaseUrl!,
    supabaseAnonKey!,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      },
    }
  );
} else {
  // Safe no-op stub: every method/chain resolves to empty data so the app renders
  // instead of crashing when Supabase isn't configured yet.
  const emptyResult = { data: null, error: null };
  const emptyArrayResult = { data: [], error: null, count: 0 };

  const makeChain = (returnArray = false): any => {
    const result = returnArray ? emptyArrayResult : emptyResult;
    const chain: any = new Proxy(() => Promise.resolve(result), {
      get: (_, prop) => {
        if (prop === 'then') return (resolve: any) => resolve(result);
        if (prop === 'catch') return () => chain;
        if (prop === 'finally') return (cb: any) => { cb?.(); return chain; };
        if (prop === 'subscribe') return (cb?: any) => { cb?.('SUBSCRIBED'); return chain; };
        if (prop === 'unsubscribe') return () => Promise.resolve('ok');
        if (prop === 'on') return () => chain;
        if (prop === 'send') return () => Promise.resolve('ok');
        return () => chain;
      },
      apply: () => Promise.resolve(result),
    });
    return chain;
  };

  supabase = new Proxy({} as SupabaseClient, {
    get: (_, prop) => {
      if (prop === 'from') return () => makeChain(true);
      if (prop === 'rpc') return () => makeChain(true);
      if (prop === 'channel') return () => makeChain();
      if (prop === 'removeChannel' || prop === 'removeAllChannels') return () => Promise.resolve('ok');
      if (prop === 'getChannels') return () => [];
      if (prop === 'storage') {
        return {
          from: () => ({
            upload: () => Promise.resolve(emptyResult),
            download: () => Promise.resolve(emptyResult),
            list: () => Promise.resolve(emptyArrayResult),
            remove: () => Promise.resolve(emptyResult),
            getPublicUrl: () => ({ data: { publicUrl: '' } }),
            createSignedUrl: () => Promise.resolve({ data: { signedUrl: '' }, error: null }),
            createSignedUrls: () => Promise.resolve(emptyArrayResult),
          }),
        };
      }
      if (prop === 'auth') {
        return {
          getUser: () => Promise.resolve({ data: { user: null }, error: null }),
          getSession: () => Promise.resolve({ data: { session: null }, error: null }),
          signInWithPassword: () => Promise.resolve(emptyResult),
          signOut: () => Promise.resolve(emptyResult),
          onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        };
      }
      if (prop === 'functions') {
        return { invoke: () => Promise.resolve(emptyResult) };
      }
      return () => makeChain();
    },
  });
}

export { supabase };

// ============================================
// Storage Helpers
// ============================================

/**
 * Get public URL for a file in a public bucket
 */
export const getPublicUrl = (bucket: string, path: string): string => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

/**
 * Get signed URL for private files (expires in 1 hour by default)
 */
export const getSignedUrl = async (
  bucket: string,
  path: string,
  expiresIn = 3600
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) throw error;
  return data.signedUrl;
};

// ============================================
// Image Compression Utilities (Storage Optimization)
// ============================================

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1
  format?: 'webp' | 'jpeg' | 'png';
}

/**
 * Compress an image file before upload to save storage space
 * Uses WebP format by default (50-80% smaller than JPEG/PNG)
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<Blob> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'webp',
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      const mimeType = format === 'webp' ? 'image/webp' :
                       format === 'jpeg' ? 'image/jpeg' : 'image/png';

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        mimeType,
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Compress image and convert to File object
 */
export const compressImageToFile = async (
  file: File,
  options: CompressionOptions = {}
): Promise<File> => {
  const format = options.format || 'webp';
  const blob = await compressImage(file, options);

  // Generate new filename with correct extension
  const originalName = file.name.replace(/\.[^/.]+$/, '');
  const newFileName = `${originalName}.${format}`;

  return new File([blob], newFileName, {
    type: blob.type,
    lastModified: Date.now(),
  });
};

/**
 * Generate a thumbnail from an image (very small, for lists/previews)
 */
export const generateThumbnail = async (
  file: File,
  maxSize = 200
): Promise<File> => {
  return compressImageToFile(file, {
    maxWidth: maxSize,
    maxHeight: maxSize,
    quality: 0.7,
    format: 'webp',
  });
};

/**
 * Extract a thumbnail from a video file
 */
export const generateVideoThumbnail = async (
  videoFile: File,
  seekTime = 1
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.onloadedmetadata = () => {
      // Seek to the specified time
      video.currentTime = Math.min(seekTime, video.duration);
    };

    video.onseeked = () => {
      // Set canvas dimensions (max 400px for thumbnail)
      const maxSize = 400;
      let { videoWidth, videoHeight } = video;

      if (videoWidth > maxSize) {
        videoHeight = (videoHeight * maxSize) / videoWidth;
        videoWidth = maxSize;
      }

      canvas.width = videoWidth;
      canvas.height = videoHeight;

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Draw frame and convert to WebP
      ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail'));
          }
        },
        'image/webp',
        0.8
      );

      // Clean up
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video'));
    };

    // Load the video
    video.src = URL.createObjectURL(videoFile);
    video.load();
  });
};

// ============================================
// File Upload Helpers
// ============================================

/**
 * Generate a unique file path for uploads
 */
export const generateFilePath = (
  folder: string,
  originalFileName: string,
  extension?: string
): string => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 9);
  const ext = extension || originalFileName.split('.').pop() || 'bin';
  const safeName = originalFileName
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .substring(0, 50);

  return `${folder}/${timestamp}-${randomId}-${safeName}.${ext}`;
};

/**
 * Upload a file with automatic compression for images
 */
export const uploadFile = async (
  bucket: string,
  folder: string,
  file: File,
  options?: {
    compress?: boolean;
    compressionOptions?: CompressionOptions;
    isPublic?: boolean;
  }
): Promise<{ path: string; url: string }> => {
  let fileToUpload: File | Blob = file;
  let extension = file.name.split('.').pop();

  // Compress images by default
  if (options?.compress !== false && file.type.startsWith('image/')) {
    try {
      fileToUpload = await compressImageToFile(file, options?.compressionOptions);
      extension = 'webp';
    } catch (err) {
      // console.warn('Image compression failed, uploading original:', err);
    }
  }

  const filePath = generateFilePath(folder, file.name, extension);

  const { error } = await supabase.storage.from(bucket).upload(filePath, fileToUpload, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error) throw error;

  // Get URL based on bucket visibility
  const url = options?.isPublic !== false
    ? getPublicUrl(bucket, filePath)
    : await getSignedUrl(bucket, filePath);

  return { path: filePath, url };
};

/**
 * Delete a file from storage
 */
export const deleteFile = async (bucket: string, path: string): Promise<void> => {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw error;
};

// ============================================
// Query Helpers
// ============================================

/**
 * Convert camelCase to snake_case for database columns
 */
export const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Convert snake_case to camelCase for JavaScript objects
 */
export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Convert object keys from camelCase to snake_case
 */
export const objectToSnakeCase = <T extends Record<string, unknown>>(
  obj: T
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[toSnakeCase(key)] = value;
    }
  }
  return result;
};

/**
 * Convert object keys from snake_case to camelCase
 */
export const objectToCamelCase = <T extends Record<string, unknown>>(
  obj: T
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toCamelCase(key)] = value;
  }
  return result;
};
